import { useEffect, useState } from "react";
import { Dnd } from "./dnd";
import { SectionDnd } from "./sectionDnd";
import toast from "react-hot-toast";
import { useBusiness } from "../business_context";

export default function QandATab() {
  const {
    isDataLoading,
    updateQaData,
    saveQaData,
    resetQaData,
    hasQaChanges,
    changedQaData,
    qaData,
    isLineActive,
  } = useBusiness();
  // Get current QA data from context
  const qa = changedQaData || qaData || [];
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Set active section when data loads or changes
  useEffect(() => {
    if (qa.length > 0) {
      // If no active section or active section doesn't exist in current data
      if (
        !activeSectionId ||
        !qa.find((section) => section.id === activeSectionId)
      ) {
        console.log(
          "QandATab: Setting activeSectionId to first section",
          qa[0].id
        );
        setActiveSectionId(qa[0].id);
      }
    }
  }, [qa, activeSectionId]);

  // Handle activeSectionId after save completion
  useEffect(() => {
    if (!isSaving && qa.length > 0) {
      // Check if current activeSectionId is valid after save
      const currentActiveSection = qa.find(
        (section) => section.id === activeSectionId
      );
      if (!currentActiveSection) {
        console.log(
          "QandATab: Active section no longer exists after save, setting to first section"
        );
        setActiveSectionId(qa[0].id);
      }
    }
  }, [isSaving, qa, activeSectionId]);

  // Add a new question to the active section
  const handleAddQuestion = () => {
    const updatedQa = qa.map((section) =>
      section.id === activeSectionId
        ? {
            ...section,
            qa_list: [
              ...section.qa_list,
              {
                id: `new-${Date.now()}`,
                sequence_no: section.qa_list.length + 1,
                question: "",
                answer: "",
                note: "",
              },
            ],
          }
        : section
    );
    updateQaData(updatedQa);
  };

  // Add a new section/tab
  const handleAddSection = () => {
    const newSectionId = `new-section-${Date.now()}`;

    // Generate a unique title by finding the next available number
    let sectionNumber = 1;
    let title = `Section ${sectionNumber}`;
    while (qa.some((section) => section.title === title)) {
      sectionNumber++;
      title = `Section ${sectionNumber}`;
    }

    const newSection = {
      id: newSectionId,
      sequence_no: qa.length + 1,
      title: title,
      description: "",
      qa_list: [],
    };
    const updatedQa = [...qa, newSection];
    updateQaData(updatedQa);
    setActiveSectionId(newSectionId);
    setEditingSectionId(newSectionId);
  };

  // Handle section title change
  const handleSectionTitleChange = (sectionId: string, value: string) => {
    // Check if the title is already used by another section
    const isDuplicate = qa.some(
      (section) => section.id !== sectionId && section.title === value
    );

    if (isDuplicate) {
      toast.error(
        "Section title must be unique. Please choose a different title."
      );
      return;
    }

    const updatedQa = qa.map((section) =>
      section.id === sectionId ? { ...section, title: value } : section
    );
    updateQaData(updatedQa);
  };

  // Handle input change for questions, answers, and notes
  const handleQaInputChange = (
    qaId: string,
    field: "question" | "answer" | "note",
    value: string
  ) => {
    const updatedQa = qa.map((section) =>
      section.id === activeSectionId
        ? {
            ...section,
            qa_list: section.qa_list.map((qaItem) =>
              qaItem.id === qaId ? { ...qaItem, [field]: value } : qaItem
            ),
          }
        : section
    );
    updateQaData(updatedQa);
  };

  // Remove a question from the active section
  const handleRemoveQuestion = (qaId: string) => {
    const updatedQa = qa.map((section) =>
      section.id === activeSectionId
        ? {
            ...section,
            qa_list: section.qa_list.filter((qaItem) => qaItem.id !== qaId),
          }
        : section
    );
    updateQaData(updatedQa);
  };

  // Handle section deletion with confirmation
  const handleDeleteSection = (sectionId: string) => {
    const sectionToDelete = qa.find((section) => section.id === sectionId);

    if (!sectionToDelete) return;

    // Prevent deleting the last section
    if (qa.length <= 1) {
      toast.error(
        "Cannot delete the last section. You must have at least one section."
      );
      return;
    }

    // Check if section has questions
    if (sectionToDelete.qa_list.length > 0) {
      // Show confirmation toast
      toast(
        (t) => (
          <div className="flex flex-col gap-2">
            <div className="font-semibold">Delete Section?</div>
            <div className="text-sm text-gray-600">
              This section contains {sectionToDelete.qa_list.length}{" "}
              question(s). Are you sure you want to delete it?
            </div>
            <div className="flex gap-2 mt-2">
              <button
                className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                onClick={() => {
                  confirmDeleteSection(sectionId);
                  toast.dismiss(t.id);
                }}
              >
                Delete
              </button>
              <button
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                onClick={() => toast.dismiss(t.id)}
              >
                Cancel
              </button>
            </div>
          </div>
        ),
        {
          duration: 0, // Don't auto-dismiss
          position: "top-center",
        }
      );
    } else {
      // No questions, delete directly
      confirmDeleteSection(sectionId);
    }
  };

  // Actually delete the section
  const confirmDeleteSection = (sectionId: string) => {
    const updatedQa = qa.filter((section) => section.id !== sectionId);

    // If we're deleting the active section, switch to another section
    if (activeSectionId === sectionId) {
      const remainingSections = updatedQa.filter(
        (section) => section.id !== sectionId
      );
      setActiveSectionId(
        remainingSections.length > 0 ? remainingSections[0].id : null
      );
    }

    updateQaData(updatedQa);
    toast.success("Section deleted successfully");
  };

  // Handle saving QA data
  const handleSave = async () => {
    // Validate that all sections have unique titles
    const sectionTitles = qa.map((section) => section.title);
    const duplicateTitles = sectionTitles.filter(
      (title, index) => sectionTitles.indexOf(title) !== index
    );

    if (duplicateTitles.length > 0) {
      const uniqueDuplicates = [...new Set(duplicateTitles)];
      toast.error(
        `Cannot save: The following section titles are duplicated: ${uniqueDuplicates.join(
          ", "
        )}. Please make all section titles unique.`
      );
      return;
    }

    // Validate that all sections have at least one Q&A item
    const emptySections = qa.filter((section) => section.qa_list.length === 0);
    if (emptySections.length > 0) {
      const sectionTitles = emptySections
        .map((section) => section.title)
        .join(", ");
      toast.error(
        `Cannot save: The following sections have no questions: ${sectionTitles}. Please add at least one question to each section.`
      );
      return;
    }

    // Validate that all Q&A items have required fields
    const invalidQaItems = qa.flatMap((section) =>
      section.qa_list.filter(
        (qaItem) => !qaItem.question.trim() || !qaItem.answer.trim()
      )
    );

    if (invalidQaItems.length > 0) {
      toast.error(
        "Cannot save: All questions must have both a question and an answer. Please fill in the missing fields."
      );
      return;
    }

    try {
      setIsSaving(true);
      console.log("QandATab: Starting save", {
        qaLength: qa.length,
        activeSectionId,
        qa: qa.map((s) => ({ id: s.id, title: s.title })),
      });

      // Update context data first to ensure it's in changedQaData
      updateQaData(qa);

      // Save to API
      await saveQaData();

      console.log("QandATab: Save completed", {
        qaLength: qa.length,
        activeSectionId,
      });

      toast.success("Q&A data saved successfully!");
      setIsSaving(false);
    } catch (err: any) {
      console.error("Error saving QA data:", err);
      const errorMessage =
        err?.response?.data?.message || "Unknown error occurred";
      toast.error(`Failed to save Q&A data. Please try again. ${errorMessage}`);
      setIsSaving(false);
    }
  };

  if (isDataLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto p-4 text-gray-500">
        Loading Q&A data...
      </div>
    );
  }

  if (!qa.length) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        {!isLineActive && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <p className="text-yellow-800 font-medium">
                Read-only mode: This line is not active. You can only view Q&A
                data.
              </p>
            </div>
          </div>
        )}

        {/* Empty State */}
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Q&A Sections Yet
            </h3>
            <p className="text-gray-500 mb-6 max-w-md">
              Create your first Q&A section to start building your knowledge
              base. You can add questions and answers to help your team respond
              to customer inquiries.
            </p>
            {isLineActive && (
              <button
                className="bg-[#4CAF71] hover:bg-[#43a15e] text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 text-lg shadow-md transition-colors"
                onClick={handleAddSection}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create First Section
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {!isLineActive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <p className="text-yellow-800 font-medium">
              Read-only mode: This line is not active. You can only view Q&A
              data.
            </p>
          </div>
        </div>
      )}

      {/* Section Tabs - Custom Styled with Drag and Drop */}
      <SectionDnd
        qa={qa}
        activeSectionId={activeSectionId}
        setQa={isLineActive ? updateQaData : () => {}}
        onSectionClick={setActiveSectionId}
        onSectionTitleChange={
          isLineActive ? handleSectionTitleChange : () => {}
        }
        onDeleteSection={isLineActive ? handleDeleteSection : () => {}}
        onAddSection={isLineActive ? handleAddSection : () => {}}
        editingSectionId={editingSectionId}
        setEditingSectionId={isLineActive ? setEditingSectionId : () => {}}
        isReadOnly={!isLineActive}
      />

      {/* Q&A Cards */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <Dnd
          qa={qa}
          activeSectionId={activeSectionId}
          setQa={
            isLineActive
              ? (updatedQa) => {
                  updateQaData(updatedQa);
                }
              : () => {}
          }
          activeId={activeId}
          setActiveId={isLineActive ? setActiveId : () => {}}
          onQaInputChange={isLineActive ? handleQaInputChange : () => {}}
          onRemoveQuestion={isLineActive ? handleRemoveQuestion : () => {}}
          isReadOnly={!isLineActive}
        />
      </div>

      {/* Add New Question Button and Save Button */}
      {isLineActive && (
        <div className="flex justify-center gap-4 mt-8">
          <button
            className="bg-[#4CAF71] hover:bg-[#43a15e] text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 text-lg shadow-md"
            onClick={handleAddQuestion}
          >
            Add New Question <span className="text-2xl leading-none">+</span>
          </button>
          {hasQaChanges() && (
            <>
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 text-lg shadow-md"
                onClick={() => resetQaData()}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 text-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
